import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';
import { documentChartsPointer } from './chartScenes';

export const documentBasicsSkill: Skill = {
    name: SKILL_NAMES.documentBasics,
    description:
        'The user attached a document (PDF, PPT, DOC). Load BEFORE analyzing it or building from it.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Document Basics

For building a video from a document the user attached. The document supplies the
content and often media; the video kind (and its skill) is whatever the user asked
for.

## Reading the document
The attachment gives you the document's extracted text and an ID. Everything else —
what each page looks like, the images on it — you request with
\`${TOOL_NAMES.documentExtract}\` as you need it. Nothing is loaded up front, so a
50-page deck costs nothing until you ask for something.

**Start with the page listing.** Call \`${TOOL_NAMES.documentExtract}\` with no pages: you get
the page count and a title per page, cheaply. That tells you what the document is and
which parts matter, which is what you need before deciding anything else.

Then request pages deliberately:
- \`text\` for the words on specific pages.
- \`screenshot\` for a picture of the page as it looks. **This is the only way to see a
  page** — a chart, a diagram, or a slide whose layout carries the meaning is invisible
  in the text alone.
- \`embedded_images\` for the individual images placed on the page, pulled out on their
  own. These are whatever the author put there — a photo, chart, diagram or screenshot,
  but just as often a logo, an icon, a headshot or decoration. A page usually has
  several, and most of them are furniture rather than content, so look before using
  one. The page listing says how many each page has.

A Word document is listed as it lays out on printed pages, and every page number you
see — listing, \`text\`, \`screenshot\` — means the same page of that layout. Words like
"Page 1" in the author's own text are just a heading and need not match; find which page
a heading sits on from the listing's titles.

**Request pages in batches, not one per call**, and only the pages you have a reason
to look at. Every page you ask for costs context, so reading a whole document page by
page when the page listing already told you which three matter is waste.

Each page you request is also registered as media, so anything you have looked at can
go straight into the video with \`${TOOL_NAMES.setMedia}\`.

## When a script is already provided
If the user's message gives you a script alongside the document, the script is what
gets narrated, word for word. The document is only reference: use it for media and
context, but never read it aloud, never merge its text into the narration, and never
write a new script from it. The document's extracted text
arriving in the same message does not make it the script.

## Scope the document first
Analyze the document's structure and scope before building. If it's long, ask what
to focus on or which section becomes the video — use \`${TOOL_NAMES.askUserQuestion}\`, with
intentional options likely to clear up the ambiguity.

### When the user asks for a page by number
A document's printed page numbers often disagree with the order its pages sit in — a
cover or agenda goes unnumbered, an appendix restarts, front matter uses roman
numerals. So "page 7" may or may not be the 7th page of the file.

**Never assume the number the user gave is the position in the file, and never build on
a page you have not confirmed.** Read the page and check it against what they asked for:

1. Read that position first (page 7 → the 7th page). Look at what came back.
2. **Confirm it is the page they meant.** If the page prints its own number, does it say
   7? Does its content match what they described? If yes, you have the right page.
3. **If it doesn't match, keep looking** — don't settle for the page you happened to
   land on. The offset is usually consistent, so a page that prints "5" when you asked
   for the 7th tells you the printed numbers run two behind: request the page that
   offset points at and check again. Scan nearby pages, or re-read the page listing to
   find the page whose printed number or content is the one they named.
4. Only ask the user (\`${TOOL_NAMES.askUserQuestion}\`) once you have looked and genuinely
   cannot tell which page they mean — for instance when two pages both print that
   number. Ask with the candidates you found, not as an opening question.

\`${TOOL_NAMES.documentExtract}\` flags documents whose stored page labels disagree with
their ordering, but a page number printed as ordinary text on the page won't be caught
that way — which is why you check the page itself rather than trusting the absence of a
warning. Showing the wrong page is the failure this avoids: it is invisible in the
finished video, because the wrong page still looks like a real page.

## The document's media
Analyze the media you take from the document and understand it in context: how does it
fit the story of the video?
- **Figures — tables, charts, diagrams, illustrations of specific concepts — need
  maximum visibility.** Pick scene designs that show the whole media without
  cropping or animation (e.g. fullbleed in landscape), consider the media's
  dimensions against the container's, and set Ken Burns to \`none\`. A figure you
  rebuild as a graphic instead keeps its look: say so in \`${TOOL_NAMES.writePlan}\`'s
  \`reproduces\`.${documentChartsPointer('  ')}
- Media that is more stock-like in nature (photos, general imagery) can be used
  liberally, like any other media.`;
    },
};
